// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable } from 'rxjs';

// --- HS Code
import {
  CommentsQuestion,
  Effectiveness,
  EffectivenessQuestionResponseValues,
  OthersScoreModel,
  ResultsViews,
  ScoreModel,
  ScoreModels,
  SurveyResultsModel,
} from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class ResultsBaseService {
  protected _surveyResults$: BehaviorSubject<SurveyResultsModel | null>;
  get surveyResults$(): Observable<SurveyResultsModel | null> {
    return this._surveyResults$;
  }

  protected _widgetDisplayMode$: BehaviorSubject<ResultsViews>;
  get widgetDisplayMode$(): Observable<ResultsViews> {
    return this._widgetDisplayMode$;
  }

  protected _scoreList$: BehaviorSubject<ScoreModels | null>;
  get scoreList$(): Observable<ScoreModels | null> {
    return this._scoreList$;
  }

  protected _areResultsLoading$: BehaviorSubject<boolean>;
  get areResultsLoading$(): Observable<boolean> {
    return this._areResultsLoading$;
  }

  constructor() {
    this._surveyResults$ = new BehaviorSubject<SurveyResultsModel | null>(null);
    this._widgetDisplayMode$ = new BehaviorSubject<ResultsViews>(
      ResultsViews.chart
    );
    this._scoreList$ = new BehaviorSubject<ScoreModels | null>(null);
    this._areResultsLoading$ = new BehaviorSubject<boolean>(true);
  }

  abstract loadSurveyResults(identifier: string): void;

  setWidgetDisplayMode(mode: ResultsViews): void {
    this._widgetDisplayMode$.next(mode);
  }

  setResultsLoading(isLoading: true) {
    this._areResultsLoading$.next(isLoading);
  }

  protected _mapScores(results: SurveyResultsModel): void {
    if (!results) {
      return;
    }

    // sort results
    if (
      results.benchmark &&
      results.benchmark.fear &&
      results.benchmark.humility &&
      results.benchmark.love &&
      results.benchmark.pride
    ) {
      results.benchmark.fear.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.humility.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.love.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (
      results.self &&
      results.self.fear &&
      results.self.humility &&
      results.self.love &&
      results.self.pride
    ) {
      results.self.fear.indicators.sort((a, b) => a.index - b.index);
      results.self.humility.indicators.sort((a, b) => a.index - b.index);
      results.self.love.indicators.sort((a, b) => a.index - b.index);
      results.self.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (
      results.others &&
      results.others.fear &&
      results.others.humility &&
      results.others.love &&
      results.others.pride
    ) {
      results.others.fear.indicators.sort((a, b) => a.index - b.index);
      results.others.humility.indicators.sort((a, b) => a.index - b.index);
      results.others.love.indicators.sort((a, b) => a.index - b.index);
      results.others.pride.indicators.sort((a, b) => a.index - b.index);
    }

    const models = {} as ScoreModels;
    models.benchmark = new Array<ScoreModel[]>();
    models.self = new Array<ScoreModel[]>();
    models.others = new Array<ScoreModel[]>();
    models.comments = new Array<CommentsQuestion>();
    models.othersComments = new Array<CommentsQuestion>();
    models.effectiveness = new Array<Effectiveness>();
    models.campaignEffectiveness = new Array<Effectiveness>();
    models.groupResults = new Array<OthersScoreModel>();

    if (results.benchmark) {
      for (let i = 0; i < 4; i++) {
        models.benchmark.push(new Array<ScoreModel>());
        models.benchmark[i].push({
          categoryName: results.benchmark.humility.indicators[i].categoryName,
          categoryType: results.benchmark.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.benchmark.humility.indicators[i].score,
          index: results.benchmark.humility.indicators[i].index,
        } as ScoreModel);
        models.benchmark[i].push({
          categoryName: results.benchmark.love.indicators[i].categoryName,
          categoryType: results.benchmark.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.benchmark.love.indicators[i].score,
          index: results.benchmark.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.benchmark) {
      for (let i = 0; i < 4; i++) {
        models.benchmark.push(new Array<ScoreModel>());
        models.benchmark[i + 4].push({
          categoryName: results.benchmark.pride.indicators[i].categoryName,
          categoryType: results.benchmark.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.benchmark.pride.indicators[i].score,
          index: results.benchmark.pride.indicators[i].index,
        } as ScoreModel);
        models.benchmark[i + 4].push({
          categoryName: results.benchmark.fear.indicators[i].categoryName,
          categoryType: results.benchmark.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.benchmark.fear.indicators[i].score,
          index: results.benchmark.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.self) {
      for (let i = 0; i < 4; i++) {
        models.self.push(new Array<ScoreModel>());
        models.self[i].push({
          categoryName: results.self.humility.indicators[i].categoryName,
          categoryType: results.self.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.self.humility.indicators[i].score,
          index: results.self.humility.indicators[i].index,
        } as ScoreModel);
        models.self[i].push({
          categoryName: results.self.love.indicators[i].categoryName,
          categoryType: results.self.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.self.love.indicators[i].score,
          index: results.self.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.self) {
      for (let i = 0; i < 4; i++) {
        models.self.push(new Array<ScoreModel>());
        models.self[i + 4].push({
          categoryName: results.self.pride.indicators[i].categoryName,
          categoryType: results.self.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.self.pride.indicators[i].score,
          index: results.self.pride.indicators[i].index,
        } as ScoreModel);
        models.self[i + 4].push({
          categoryName: results.self.fear.indicators[i].categoryName,
          categoryType: results.self.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.self.fear.indicators[i].score,
          index: results.self.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.others) {
      for (let i = 0; i < 4; i++) {
        if (
          results.others.humility.indicators.length === 0 ||
          results.others.love.indicators.length === 0
        ) {
          break;
        }
        models.others.push(new Array<ScoreModel>());
        models.others[i].push({
          categoryName: results.others.humility.indicators[i].categoryName,
          categoryType: results.others.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.others.humility.indicators[i].score,
          index: results.others.humility.indicators[i].index,
        } as ScoreModel);
        models.others[i].push({
          categoryName: results.others.love.indicators[i].categoryName,
          categoryType: results.others.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.others.love.indicators[i].score,
          index: results.others.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.others) {
      for (let i = 0; i < 4; i++) {
        if (
          results.others.pride.indicators.length === 0 ||
          results.others.fear.indicators.length === 0
        ) {
          break;
        }
        models.others.push(new Array<ScoreModel>());
        models.others[i + 4].push({
          categoryName: results.others.pride.indicators[i].categoryName,
          categoryType: results.others.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.others.pride.indicators[i].score,
          index: results.others.pride.indicators[i].index,
        } as ScoreModel);
        models.others[i + 4].push({
          categoryName: results.others.fear.indicators[i].categoryName,
          categoryType: results.others.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.others.fear.indicators[i].score,
          index: results.others.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.groupResults) {
      results.groupResults.forEach((group) => {
        const groupResultsModels = new Array<ScoreModel[]>();

        if (
          group.others &&
          group.others.fear &&
          group.others.humility &&
          group.others.love &&
          group.others.pride
        ) {
          group.others.fear.indicators.sort((a, b) => a.index - b.index);
          group.others.humility.indicators.sort((a, b) => a.index - b.index);
          group.others.love.indicators.sort((a, b) => a.index - b.index);
          group.others.pride.indicators.sort((a, b) => a.index - b.index);
        }

        if (group.others) {
          for (let i = 0; i < 4; i++) {
            if (
              group.others.humility.indicators.length === 0 ||
              group.others.love.indicators.length === 0
            ) {
              break;
            }
            groupResultsModels.push(new Array<ScoreModel>());
            groupResultsModels[i].push({
              categoryName: group.others.humility.indicators[i].categoryName,
              categoryType: group.others.humility.indicators[i].categoryType,
              quadrantName: 'humility',
              score: group.others.humility.indicators[i].score,
              index: group.others.humility.indicators[i].index,
            } as ScoreModel);
            groupResultsModels[i].push({
              categoryName: group.others.love.indicators[i].categoryName,
              categoryType: group.others.love.indicators[i].categoryType,
              quadrantName: 'love',
              score: group.others.love.indicators[i].score,
              index: group.others.love.indicators[i].index,
            } as ScoreModel);
          }
        }
        if (group.others) {
          for (let i = 0; i < 4; i++) {
            if (
              group.others.pride.indicators.length === 0 ||
              group.others.fear.indicators.length === 0
            ) {
              break;
            }
            groupResultsModels.push(new Array<ScoreModel>());
            groupResultsModels[i + 4].push({
              categoryName: group.others.pride.indicators[i].categoryName,
              categoryType: group.others.pride.indicators[i].categoryType,
              quadrantName: 'pride',
              score: group.others.pride.indicators[i].score,
              index: group.others.pride.indicators[i].index,
            } as ScoreModel);
            groupResultsModels[i + 4].push({
              categoryName: group.others.fear.indicators[i].categoryName,
              categoryType: group.others.fear.indicators[i].categoryType,
              quadrantName: 'fear',
              score: group.others.fear.indicators[i].score,
              index: group.others.fear.indicators[i].index,
            } as ScoreModel);
          }
        }
        models.groupResults.push({
          groupName: group.groupName,
          numberOfCompleteResponses: group.numberOfCompleteResponses,
          respondentNames: group.respondentNames,
          respondentNamesList: group.respondentNamesList,
          results: groupResultsModels,
        });
      });
    }

    if (results.commentsQuestionPages) {
      let selfComments = new Array<CommentsQuestion>();
      let othersComments = new Array<CommentsQuestion>();
      results.commentsQuestionPages.forEach((c) => {
        if (!Array.isArray(c.sections)) {
          return;
        }
        const selfSections = c.sections.find((s) => s.sectionName === 'Self');
        const othersSections = c.sections.find(
          (s) => s.sectionName === 'Others'
        );
        if (selfSections) {
          selfComments = [...selfComments, ...selfSections.commentsQuestions];
        }

        if (othersSections) {
          othersComments = [
            ...othersComments,
            ...othersSections.commentsQuestions,
          ];
        }
      });
      models.comments = selfComments;
      models.othersComments = othersComments;
    }

    if (
      results.effectivenessModel &&
      results.effectivenessModel.effectiveness
    ) {
      if (Array.isArray(results.effectivenessModel.effectiveness)) {
        models.effectiveness = results.effectivenessModel.effectiveness.map(
          this._mapEffectivenessScores.bind(this)
        );
      }
    }

    if (
      results.campaignEffectivenessModel &&
      results.campaignEffectivenessModel.effectiveness
    ) {
      if (Array.isArray(results.campaignEffectivenessModel.effectiveness)) {
        models.campaignEffectiveness =
          results.campaignEffectivenessModel.effectiveness.map(
            this._mapEffectivenessScores.bind(this)
          );
      }
    }

    this._scoreList$.next(models);
  }

  protected _getScoreText(score: number): string {
    switch (score) {
      case EffectivenessQuestionResponseValues.veryLow: {
        return 'effectivenessVeryLow';
      }
      case EffectivenessQuestionResponseValues.low: {
        return 'effectivenessLow';
      }
      case EffectivenessQuestionResponseValues.moderate: {
        return 'effectivenessModerate';
      }
      case EffectivenessQuestionResponseValues.high: {
        return 'effectivenessHigh';
      }
      default: {
        return 'effectivenessVeryHigh';
      }
    }
  }

  protected _mapEffectivenessScores(effectiveness: Effectiveness) {
    effectiveness.selfScorePercentage = (effectiveness.selfScore / 4) * 100;
    effectiveness.otherScorePercentage = (effectiveness.otherScore / 4) * 100;
    effectiveness.otherMaximumPercentage =
      ((effectiveness.otherMaximum - effectiveness.otherMinimum) / 4) * 100;
    effectiveness.otherMinimumPercentage =
      (effectiveness.otherMinimum / 4) * 100;
    effectiveness.selfScoreText = this._getScoreText(effectiveness.selfScore);
    effectiveness.otherScoreText = this._getScoreText(effectiveness.otherScore);
    return effectiveness;
  }
}
